import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {CognitoUserAttribute, CognitoUserPool, CognitoUser, AuthenticationDetails, UserData, CognitoUserSession}  from 'amazon-cognito-identity-js';
import * as AWS from 'aws-sdk/global';

import { User } from '../models/AccountModel';

@Injectable()
export class CognitoService {

  private static POOLDATA = {
    UserPoolId : environment.cognito.userPoolId,
    ClientId : environment.cognito.clientId
  };

  public static _REGION = environment.cognito.region;
  public static _IDENTITY_POOL_ID = environment.cognito.identityPoolId;
  public static _USER_POOL_ID = environment.cognito.userPoolId;
  public static _CLIENT_ID = environment.cognito.clientId;

  public static _POOL_DATA: any = {
      UserPoolId: CognitoService._USER_POOL_ID,
      ClientId: CognitoService._CLIENT_ID
  };

  public cognitoCreds: AWS.CognitoIdentityCredentials;

  buildAttributeList(user: User):CognitoUserAttribute[] {
    const attrs = [];

    for (const key of user.cognitoAttributes) {
      const attrObj = {
        Name: key,
        Value: user[key]
      }
      console.log(attrObj);
      attrs.push(new CognitoUserAttribute(attrObj));
    }

    return attrs;
  }

  register(user: User): Promise<any> {
    return new Promise((resolve, reject) => {
      const userPool = new CognitoUserPool(CognitoService.POOLDATA);
      userPool.signUp(user.email, user.password, this.buildAttributeList(user), null, (err, result) => {
        if (err) {
          reject(err);
        }
        resolve(result);
      });
    });
  }

  confirmRegistration(username:string, confirmationCode: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const userData = {
        Username : username,
        Pool : new CognitoUserPool(CognitoService.POOLDATA)
      };

      const cognitoUser = new CognitoUser(userData);
      cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) {
          reject(err.message);
        } else {
          resolve(result);
        }
      });
    });
  }

  resendCode(username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const userData = {
        Username: username,
        Pool: new CognitoUserPool(CognitoService.POOLDATA)
      };

      const cognitoUser = new CognitoUser(userData);
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err.message);
        } else {
          resolve(result);
        }
      });
    });
  }

  getParameters(): Promise<any> {
    return new Promise((resolve, reject) => {

      const cognitoUser = this.getCurrentUser();

      if (cognitoUser != null) {
        cognitoUser.getSession((serr, session) => {
          if (serr) {
            reject(serr);
          } else {
            cognitoUser.getUserAttributes((err, result) => {
              if (err) {
                reject(err);
              } else {
                resolve(result);
              }
            });
          }
        });
      } else {
        resolve(null);
      }
    });
  }

  getIdToken(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser() != null) {
        this.getCurrentUser().getSession((err, session) => {
          if (err) {
            reject(err);
          }
          else {
            if (session.isValid()) {
              resolve(session.getIdToken().getJwtToken());
            } else {
              resolve(null);
            }
          }
        });
      }
      else {
        resolve(null);
      }
    })
  }

  getJwts(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser() != null) {
        this.getCurrentUser().getSession((err, session) => {
          if (err) {
            reject(err);
          }
          else {
            if (session.isValid()) {
              resolve({
                accessToken: session.getAccessToken().getJwtToken(),
                idToken: session.getIdToken().getJwtToken()
              });
            } else {
              resolve(null);
            }
          }
        });
      }
      else {
        resolve(null);
      }
    })
  }

  authenticate(username: string, password: string): Promise<any> {
    const self = this;
    return new Promise((resolve, reject) => {
      const authenticationData = {
        Username : username,
        Password : password,
      };
      const authenticationDetails = new AuthenticationDetails(authenticationData);
      const userData = {
          Username : username,
          Pool : new CognitoUserPool(CognitoService.POOLDATA)
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.authenticateUser(authenticationDetails, {
        newPasswordRequired (userAttributes, requiredAttributes) {
          resolve({status:'NEWPASSWORD', userAttributes, requiredAttributes});
        },
        onSuccess (session) {
          self.handleLoginSuccess(session, resolve, reject);
        },

        mfaSetup: function(challengeName, challengeParameters) {
          console.log('mfaSetup', challengeName, challengeParameters)
          cognitoUser.associateSoftwareToken(
            {
            associateSecretCode: function(secretCode) {
              resolve({status:'SETUPTOTPMFA', secretCode, cognitoUser});
            },
            onFailure: reject
          });
        },

        totpRequired(secretCode) {
          console.log(cognitoUser);
          resolve({status:'MFAREQUIRED', secretCode, cognitoUser});
        },

        onFailure: reject
      });
    })
  }

  forgotPassword(username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const userData = {
          Username : username,
          Pool : new CognitoUserPool(CognitoService.POOLDATA)
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.forgotPassword({
        onSuccess (result) {
          console.log(result);
          resolve('SUCCESS');
        },
        onFailure(err) {
          reject(err);
        }
      });
    })
  }

  resetPassword(username: string, verificationCode: string, password: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const userData = {
          Username : username,
          Pool : new CognitoUserPool(CognitoService.POOLDATA)
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.confirmPassword(verificationCode, password, {
        onSuccess () {
          resolve('SUCCESS');
        },
        onFailure(err) {
          reject(err);
        }
      });
    })
  }

  newPassword(username: string, existingPassword: string, newPassword: string): Promise<any> {
    const self = this;
    return new Promise((resolve, reject) => {
      const authenticationData = {
        Username : username,
        Password : existingPassword,
      };
      const authenticationDetails = new AuthenticationDetails(authenticationData);
      const userData = {
          Username : username,
          Pool : new CognitoUserPool(CognitoService.POOLDATA)
      };
      const cognitoUser = new CognitoUser(userData);
      cognitoUser.authenticateUser(authenticationDetails, {
        newPasswordRequired (userAttributes, requiredAttributes) {
          // const userData = {
          //   Username : username,
          //   Pool : new CognitoUserPool(CognitoService.POOLDATA)
          // };
          cognitoUser.completeNewPasswordChallenge(newPassword, requiredAttributes, this);
        },
        mfaSetup: function(challengeName, challengeParameters) {
          console.log('mfaSetup', challengeName, challengeParameters)
          cognitoUser.associateSoftwareToken(
            {
            associateSecretCode: function(secretCode) {
              resolve({status:'SETUPTOTPMFA', secretCode, cognitoUser});
            },
            onFailure: reject
          });
        },
        onSuccess (session) {
          self.handleLoginSuccess(session, resolve, reject);
        },
        onFailure(err) {
          reject(err);
        }
      });
    })
  }

  changePassword(existingPassword: string, newPassword: string) : Promise<any> {
    return new Promise((resolve, reject) => {

      const cognitoUser = this.getCurrentUser();
      cognitoUser.getSession((serr, session) => {
        cognitoUser.changePassword(existingPassword, newPassword, (err, result) => {
          if (err) {
            reject(err);
          } else {
            resolve(result);
          }
        });
      })
    });
  }

  getCurrentUser() {
      return new CognitoUserPool(CognitoService.POOLDATA).getCurrentUser();
  }

  // Step 1: display QR to user
  getMfaSecretCode() : Promise<any> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
        } else {
          cognitoUser.associateSoftwareToken({
            associateSecretCode: resolve,
            onFailure: reject
          });
        }
      });
    });
  }

  // Step 2: enter TOTP from authenticator
  verifyTotpCode(totpCode) {
    console.log('setting', totpCode);
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
        } else {
          cognitoUser.verifySoftwareToken(totpCode, 'Software Authenticator', {
            onSuccess: resolve,
            onFailure: reject
          });
        }
      });
    });
  }

  // Step 3: enable MFA
  enableMfa() {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      cognitoUser.getSession((serr, session) => {
        const totpMfaSettings = {
          PreferredMfa: true,
          Enabled: true,
        };
        cognitoUser.setUserMfaPreference(null, totpMfaSettings, (err, result) => {
          if (err) {
            reject(err);
          } else {
            (cognitoUser as any).clearCachedUserData();
            resolve(result);
          }
        });
      });
    });
  }

  disableMfa() {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();
      cognitoUser.getSession((serr, session) => {
        cognitoUser.disableMFA((err, result) => {
          if (err) {
            reject(err);
          } else {
            const totpMfaSettings = {
              PreferredMfa: false,
              Enabled: false,
            };
            cognitoUser.setUserMfaPreference(null, totpMfaSettings, (merr, mresult) => {
              if (merr) {
                reject(merr);
              } else {
                (cognitoUser as any).clearCachedUserData();
                resolve(mresult);
              }
            });
          }
        });
      });
    });
  }

  // Authenticate MFA token
  sendMfaToken(cognitoUser:CognitoUser, totpToken:string, setup:boolean = false) {
    const self = this;
    return new Promise((resolve, reject) => {
      if (setup) {
        cognitoUser.verifySoftwareToken(totpToken, 'Software Authenticator', {
          onSuccess (session) {
            const totpMfaSettings = {
              PreferredMfa: true,
              Enabled: true,
            };
            cognitoUser.setUserMfaPreference(null, totpMfaSettings, (err, result) => {
              if (err) {
                reject(err);
              } else {
                (cognitoUser as any).clearCachedUserData();
                self.handleLoginSuccess(session, resolve, reject);
              }
            });
            
          },
          onFailure: reject
        });
      } else {
        cognitoUser.sendMFACode(totpToken, {
          onSuccess (session) {
            self.handleLoginSuccess(session, resolve, reject);
          },
          onFailure: reject
        }, 'SOFTWARE_TOKEN_MFA');
      }
    });
  }

  getMfaStatus() : Promise<string> {
    return new Promise((resolve, reject) => {
      const cognitoUser:CognitoUser = this.getCurrentUser();
      cognitoUser.getSession((err, session) => {
        // Need to cast this to any because the latest cognito typescript definition doesn't support the params propery of getUserData
        // even though it is in the javascript source
        cognitoUser.getMFAOptions((ee, rr) => {
          console.log('getmfaoptions', ee, rr);
        });
        cognitoUser.getUserData((err2, result:UserData) => {
          console.log('getuserdata', result);
          if (err2) {
            reject(err2);
          } else {
            resolve(result.PreferredMfaSetting);
          }
        });
      });
    })
  }

  handleLoginSuccess(userSession: CognitoUserSession, resolve, reject) {
    AWS.config.region = environment.cognito.region;
    const identityOptions = {
      IdentityPoolId: environment.cognito.identityPoolId,
      Logins: { }
    };
    identityOptions.Logins[environment.cognito.idp_endpoint] = userSession.getIdToken().getJwtToken();

    AWS.config.credentials = new AWS.CognitoIdentityCredentials(identityOptions);
    // refreshes credentials using AWS.CognitoIdentity.getCredentialsForIdentity()
    console.log((AWS.config.credentials as AWS.CognitoIdentityCredentials));
    (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh((error) => {
      if (error) {
        reject(error)
      } else {
        resolve('SUCCESS');
      }
    });
  }
}